import React, { useState, useEffect } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toastActions } from '../../../../components/uielements/toast/toast';
import { IntlMessages } from '../../../../helper/langHelper';
import AdminModal from '../../../../components/uielements/modal/adminModal';

import {
  FormWrapper,
  FormSection,
  FieldGroup,
  Label,
  ErrorText,
  ButtonContainer,
  PrimaryButton,
} from '../../users/modals/UserPromoteModal.styles';
import { createProcedure, updateProcedure } from '../../../../services/admin/procedures';
import PdfMultiSelect from '../Pdfmultiselect';

const CreateAndEditProcedureModal = ({
  onClose,
  isEdit = false,
  procedureId = null,
  initialData = null,
}) => {
  const queryClient = useQueryClient();

  // Form state
  const [procedureName, setProcedureName] = useState('');
  const [defination, setDefination] = useState('');
  const [selectedPdfs, setSelectedPdfs] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [apiError, setApiError] = useState('');

  // Initialize with edit data
  useEffect(() => {
    if (isEdit && initialData) {
      setProcedureName(initialData.procedureName || '');
      setDefination(initialData.defination || '');
      setSelectedPdfs(initialData.pdfArray || []);
    }
  }, [isEdit, initialData]);

  // Create Mutation
  const createMutation = useMutation({
    mutationFn: (payload) => createProcedure(payload),
    onSuccess: () => {
      toastActions.success(
        <IntlMessages
          id="toast.procedure.add.success"
          defaultMessage="Procedure created successfully."
        />
      );
      queryClient.invalidateQueries({ queryKey: ['adminProcedures'] });
      handleClose();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create procedure. Please try again.';

      toastActions.error(
        <IntlMessages id="toast.procedure.add.error" defaultMessage="Failed to create procedure." />
      );
      setApiError(errorMessage);
    },
  });

  // Update Mutation
  const updateMutation = useMutation({
    mutationFn: (payload) => updateProcedure(payload),
    onSuccess: () => {
      toastActions.success(
        <IntlMessages
          id="toast.procedure.update.success"
          defaultMessage="Procedure updated successfully."
        />
      );
      queryClient.invalidateQueries({ queryKey: ['adminProcedures'] });
      handleClose();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update procedure. Please try again.';

      toastActions.error(
        <IntlMessages
          id="toast.procedure.update.error"
          defaultMessage="Failed to update procedure."
        />
      );
      setApiError(errorMessage);
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!procedureName.trim()) {
      errors.procedureName = 'Procedure name is required.';
    }

    if (!defination.trim()) {
      errors.defination = 'Definition is required.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) {
      return;
    }

    const payload = {
      procedureName: procedureName.trim(),
      defination: defination.trim(),
      pdfArray: selectedPdfs,
    };

    console.log('Submitting payload:', payload); // Debug log

    if (isEdit) {
      updateMutation.mutate({
        ...payload,
        procedureId,
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  // Handle Close
  const handleClose = () => {
    setProcedureName('');
    setDefination('');
    setSelectedPdfs([]);
    setValidationErrors({});
    setApiError('');
    onClose();
  };

  // Handle Name Change
  const handleProcedureNameChange = (e) => {
    const value = e.target.value;
    setProcedureName(value);
    if (validationErrors.procedureName && value.trim()) {
      setValidationErrors((prev) => ({
        ...prev,
        procedureName: '',
      }));
    }
  };

  // Handle Definition Change
  const handleDefinationChange = (e) => {
    const value = e.target.value;
    setDefination(value);
    if (validationErrors.defination && value.trim()) {
      setValidationErrors((prev) => ({
        ...prev,
        defination: '',
      }));
    }
  };

  // Handle PDF Selection Change
  const handlePdfChange = (newSelection) => {
    console.log('PDF Selection changed:', newSelection); // Debug log
    setSelectedPdfs(newSelection);
  };

  // Footer
  const footer = (
    <ButtonContainer>
      <PrimaryButton
        type="submit"
        form="procedure-form"
        disabled={
          isLoading ||
          !procedureName.trim() ||
          !defination.trim() ||
          Object.keys(validationErrors).length > 0
        }
      >
        {isLoading
          ? isEdit
            ? 'Updating…'
            : 'Creating…'
          : isEdit
            ? 'Update Procedure'
            : 'Create Procedure'}
      </PrimaryButton>
    </ButtonContainer>
  );

  return (
    <AdminModal
      isOpen={true}
      onClose={handleClose}
      title={isEdit ? 'Edit Procedure' : 'Create Procedure'}
      footer={footer}
      width="580px"
    >
      <FormWrapper id="procedure-form" onSubmit={handleSubmit}>
        {/* API Error */}
        {apiError && (
          <div
            style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px',
              border: '1px solid #ef5350',
            }}
          >
            {apiError}
          </div>
        )}

        {/* Procedure Name */}
        <FormSection>
          <FieldGroup>
            <Label htmlFor="procedure-name">Procedure Name *</Label>
            <input
              id="procedure-name"
              type="text"
              value={procedureName}
              onChange={handleProcedureNameChange}
              placeholder="Enter procedure name..."
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: validationErrors.procedureName ? '1px solid #d32f2f' : '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s ease',
                backgroundColor: isLoading ? '#f5f5f5' : '#fff',
                cursor: isLoading ? 'not-allowed' : 'text',
              }}
            />
            {validationErrors.procedureName && (
              <ErrorText>{validationErrors.procedureName}</ErrorText>
            )}
          </FieldGroup>
        </FormSection>

        {/* Definition */}
        <FormSection>
          <FieldGroup>
            <Label htmlFor="definition">Definition *</Label>
            <textarea
              id="definition"
              value={defination}
              onChange={handleDefinationChange}
              placeholder="Enter procedure definition..."
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: validationErrors.defination ? '1px solid #d32f2f' : '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                minHeight: '100px',
                resize: 'vertical',
                transition: 'border-color 0.2s ease',
                backgroundColor: isLoading ? '#f5f5f5' : '#fff',
                cursor: isLoading ? 'not-allowed' : 'text',
              }}
            />
            {validationErrors.defination && <ErrorText>{validationErrors.defination}</ErrorText>}
          </FieldGroup>
        </FormSection>

        {/* PDF Selection */}
        <FormSection>
          <FieldGroup>
            <PdfMultiSelect
              selectedPdfIds={selectedPdfs}
              onChange={handlePdfChange}
              disabled={isLoading}
            />
          </FieldGroup>
        </FormSection>
      </FormWrapper>
    </AdminModal>
  );
};

export default CreateAndEditProcedureModal;
